import { useEffect, useState } from "react";

import { Loader2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useOptimizedSellers } from "~/hooks/useOptimizedProfiles";
import { ClientApprovalRequest } from "~/types/clientApproval";

const NO_SELLER = "__none__";

interface ApproveDialogProps {
  request: ClientApprovalRequest | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (assignedSeller: string | null) => Promise<void>;
  isSubmitting: boolean;
}

export function ApproveDialog({
  request,
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
}: ApproveDialogProps) {
  const { sellers, isLoading: sellersLoading } = useOptimizedSellers();
  const [seller, setSeller] = useState<string>(NO_SELLER);

  // Reset the seller pick each time the dialog opens for a new request.
  useEffect(() => {
    if (open) setSeller(NO_SELLER);
  }, [open, request?.id]);

  const handleConfirm = async () => {
    await onConfirm(seller === NO_SELLER ? null : seller);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Aprobar cliente</DialogTitle>
          <DialogDescription>
            Se creará el cliente <span className="font-medium">{request?.name}</span>{" "}
            a partir de esta solicitud. Puedes asignar un vendedor ahora o dejarlo
            para más tarde.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <Label htmlFor="approve-seller">Vendedor asignado (opcional)</Label>
          <Select value={seller} onValueChange={setSeller}>
            <SelectTrigger id="approve-seller" className="h-9">
              <SelectValue
                placeholder={
                  sellersLoading ? "Cargando…" : "Sin asignar"
                }
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_SELLER}>Sin asignar</SelectItem>
              {sellers.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Aprobar y crear cliente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
